import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  HomeOutlined,
  ShopOutlined,
  UnorderedListOutlined,
  CloudServerOutlined,
  SettingOutlined,
  RocketOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
  LogoutOutlined
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { menuApi } from '../services/api';
import type { FrontendMenu } from '../types';

interface SidebarNexusProps {
  collapsed: boolean;
  onCollapse: (collapsed: boolean) => void;
}

// 图标映射函数
const getIconComponent = (iconName: string | null | undefined) => {
  const iconMap: Record<string, React.ReactNode> = {
    'HomeOutlined': <HomeOutlined />,
    'ShopOutlined': <ShopOutlined />,
    'UnorderedListOutlined': <UnorderedListOutlined />,
    'CloudServerOutlined': <CloudServerOutlined />,
    'SettingOutlined': <SettingOutlined />,
  };
  return iconName ? iconMap[iconName] || <HomeOutlined /> : <HomeOutlined />;
};

const SidebarNexus: React.FC<SidebarNexusProps> = ({ collapsed, onCollapse }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAuthLoading } = useAuth();
  const [menuItems, setMenuItems] = useState<Array<{ key: string; icon: React.ReactNode; label: string }>>([]);
  const [loading, setLoading] = useState(true);

  // 从数据库加载菜单数据
  useEffect(() => {
    const loadMenus = async () => {
      try {
        const response = await menuApi.getMenus();
        if (response.success && response.data) {
          // 只获取一级菜单（menu_level = 1）且显示的菜单（is_show = 1）
          const level1Menus = response.data
            .filter((menu: FrontendMenu) => menu.menu_level === 1)
            .sort((a: FrontendMenu, b: FrontendMenu) => a.display_order - b.display_order)
            .map((menu: FrontendMenu) => ({
              key: menu.route_path || '/',
              icon: getIconComponent(menu.icon_name),
              label: menu.menu_name
            }));
          setMenuItems(level1Menus);
        }
      } catch (error) {
        console.error('加载菜单失败:', error);
        // 失败时使用默认菜单
        setMenuItems([
          { key: '/', icon: <HomeOutlined />, label: '首页' },
          { key: '/workflow-market', icon: <ShopOutlined />, label: '工作流市场' },
          { key: '/task-queue', icon: <UnorderedListOutlined />, label: '任务队列' },
          { key: '/cloud-server', icon: <CloudServerOutlined />, label: '云服务器' },
          { key: '/settings', icon: <SettingOutlined />, label: '设置' }
        ]);
      } finally {
        setLoading(false);
      }
    };

    loadMenus();
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <div 
      className={`fixed left-0 top-0 h-screen bg-gradient-to-b from-gray-900 to-gray-800 text-white transition-all duration-300 z-40 border-r border-gray-700 flex flex-col ${
        collapsed ? 'w-20' : 'w-64'
      }`}
    >
      {/* Logo Section */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-gray-700">
        {!collapsed && (
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-xl flex items-center justify-center">
              <RocketOutlined style={{ fontSize: '20px', color: 'white' }} />
            </div>
            <span className="text-lg font-bold">{user?.system_title || '集木视频系统'}</span>
          </div>
        )}
        {collapsed && (
          <div className="w-10 h-10 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-xl flex items-center justify-center mx-auto">
            <RocketOutlined style={{ fontSize: '20px', color: 'white' }} />
          </div>
        )}
      </div>

      {/* Toggle Button */}
      <button
        onClick={() => onCollapse(!collapsed)}
        className="absolute -right-3 top-20 w-6 h-6 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all z-50"
      >
        {collapsed ? (
          <MenuUnfoldOutlined style={{ fontSize: '12px', color: 'white' }} />
        ) : (
          <MenuFoldOutlined style={{ fontSize: '12px', color: 'white' }} />
        )}
      </button>

      {/* Menu Items */}
      <nav className="mt-4 px-2 flex-1">
        {loading ? (
          <div className="flex justify-center items-center py-8">
            <div className="text-gray-400 text-sm">加载中...</div>
          </div>
        ) : (
          menuItems.map((item) => (
          <button
            key={item.key}
            onClick={() => navigate(item.key)}
            className={`w-full flex items-center space-x-3 px-4 py-3 mb-2 rounded-xl transition-all ${
              isActive(item.key)
                ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg'
                : 'text-gray-300 hover:bg-gray-700/50 hover:text-white'
            }`}
            title={collapsed ? item.label : ''}
          >
            <span className="text-xl">{item.icon}</span>
            {!collapsed && <span className="font-medium">{item.label}</span>}
          </button>
        ))
        )}
      </nav>

      {/* User Section */}
      <div className="px-2 pb-4 border-t border-gray-700 pt-4">
        {!collapsed && user && (
          <div className="px-4 py-3 bg-gray-700/30 rounded-xl mb-2">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-full flex items-center justify-center">
                <UserOutlined style={{ fontSize: '18px', color: 'white' }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-white text-sm font-medium truncate">{user.user_name || 'admin'}</div>
                <div className="text-gray-400 text-xs truncate">
                  {isAuthLoading ? '加载中...' : (user.user_type === 'trial' ? '试用用户' : '专业用户')}
                </div>
                {!isAuthLoading && user.user_type === 'trial' && (() => {
                  const remaining = (user.trial_times ?? 0) - (user.used_times ?? 0);
                  const isLow = remaining <= 3;
                  const isExhausted = remaining <= 0;
                  return (
                    <div className="text-xs mt-1" style={{ color: isExhausted ? '#ff4d4f' : isLow ? '#faad14' : '#8b949e' }}>
                      {isExhausted ? '试用已耗尽' : `剩余 ${remaining} / ${user.trial_times} 次`}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        )}
        {collapsed && user && (
          <div className="flex justify-center mb-2">
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-full flex items-center justify-center">
              <UserOutlined style={{ fontSize: '18px', color: 'white' }} />
            </div>
          </div>
        )}
        {/* <button
          onClick={() => navigate('/profile')}
          className={`w-full flex items-center space-x-3 px-4 py-3 mb-2 rounded-xl transition-all ${
            isActive('/profile')
              ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg'
              : 'text-gray-300 hover:bg-gray-700/50 hover:text-white'
          }`}
          title={collapsed ? '个人中心' : ''}
        >
          <UserOutlined style={{ fontSize: '18px' }} />
          {!collapsed && <span className="font-medium">个人中心</span>}
        </button> */}
        <button
          onClick={handleLogout}
          className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all text-gray-300 hover:bg-red-600/20 hover:text-red-400"
          title={collapsed ? '退出登录' : ''}
        >
          <LogoutOutlined style={{ fontSize: '18px' }} />
          {!collapsed && <span className="font-medium">退出登录</span>}
        </button>
      </div>
    </div>
  );
};

export default SidebarNexus;
